import type { InsightsInsightsPageQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { ExportRow, GenerateExport } from './export'

type EmployeesGenderHeadcount =
  InsightsInsightsPageQuery['employees']['genderHeadcountsConnection']['nodes'][number]

const DEFAULT_FEMALE_GENDER = 'female'
const DEFAULT_MALE_GENDER = 'male'
const DEFAULT_GENDERS = [DEFAULT_FEMALE_GENDER, DEFAULT_MALE_GENDER]

const translateGender = (gender: string): string => {
  switch (gender) {
    case DEFAULT_MALE_GENDER:
      return i18n.t('common.male')
    case DEFAULT_FEMALE_GENDER:
      return i18n.t('common.female')
    default:
      return gender
  }
}

const headcountByGenderData = (headcountByGender: EmployeesGenderHeadcount[]) => {
  const data = headcountByGender.map((datum) => ({
    id: datum.genderName,
    originalLabel: datum.genderName,
    label: translateGender(datum.genderName),
    value: datum.headcount,
  }))

  // Sort everything alphabetically, but default genders first
  const female = data.find((datum) => datum.originalLabel == DEFAULT_FEMALE_GENDER)
  const male = data.find((datum) => datum.originalLabel == DEFAULT_MALE_GENDER)
  const others = data
    .filter((datum) => !datum.originalLabel || !DEFAULT_GENDERS.includes(datum.originalLabel))
    .sort((a, b) => a.label.localeCompare(b.label))
  return [female, male, ...others].flatMap((datum) => (datum !== undefined ? [datum] : []))
}

export class HeadcountByGender implements GenerateExport {
  private headcountByGender: EmployeesGenderHeadcount[]

  constructor(headcountByGender: EmployeesGenderHeadcount[]) {
    this.headcountByGender = headcountByGender
  }

  isEmpty() {
    return !this.headcountByGender.length
  }

  export() {
    const data: ExportRow[][] = [
      [i18n.t('common.gender'), i18n.t('insights.widgets.shared.headcount')],
    ]

    this.generate().forEach(({ label, value }) => {
      data.push([label, value])
    })

    return {
      title: i18n.t('insights.widgets.headcount_by_gender.title'),
      data: data,
    }
  }

  generate() {
    return headcountByGenderData(this.headcountByGender)
  }
}
